import { TFunction } from 'react-i18next';
import { FeedbackType } from '@/models/feedback.model';
import { PotentialPartnersContainer } from '@/modules/Partners';
import {
  ContactSupportContainer,
  OrderQuestionsContainer,
  PartnersRequestsContainer,
  ProductAvailabilityContainer,
  UsersFeedbackContainer,
} from '../containers';
import { StyledTabLabelWithBadge, StyledTabNotificationBadge } from '../components/Styled';
import { sumNewFeedbacksCountByTypes } from '../feature';

export enum FeedbackTabs {
  USERS_REQUESTS = 'users_requests',
  POTENTIAL_PARTNERS = 'potential_partners',
  PARTNERS_REQUESTS = 'partners_requests',
}

export enum UsersRequestsTabs {
  CONTACT = 'contact',
  PRODUCT_AVAILABILITY = 'product_availability',
  ORDER_QUESTIONS = 'order_questions',
}

export const USERS_REQUESTS_FEEDBACK_TYPES: FeedbackType[] = [
  FeedbackType.CONTACT_PAGE,
  FeedbackType.PRODUCT_AVAILABILITY,
  FeedbackType.ORDER_SUPPORT,
];

const formatBadgeCount = (count: number): string => (count > 99 ? '99+' : String(count));

const renderTabLabel = (label: string, count: number) => (
  <StyledTabLabelWithBadge>
    <span>{label}</span>
    {count > 0 && <StyledTabNotificationBadge>{formatBadgeCount(count)}</StyledTabNotificationBadge>}
  </StyledTabLabelWithBadge>
);

export const getTabsConfig = (
  t: TFunction,
  newCountByType: Record<FeedbackType, number>,
  canManageFeedback = false,
) => {
  const usersRequestsCount = sumNewFeedbacksCountByTypes(newCountByType, USERS_REQUESTS_FEEDBACK_TYPES);
  const potentialPartnersCount = newCountByType[FeedbackType.POTENTIAL_PARTNER] ?? 0;
  const partnersRequestsCount = newCountByType[FeedbackType.PARTNER_SUPPORT] ?? 0;

  const tabs = [
    {
      label: renderTabLabel(t('tab_users_requests'), usersRequestsCount),
      key: FeedbackTabs.USERS_REQUESTS,
      content: <UsersFeedbackContainer />,
    },
    {
      label: renderTabLabel(t('tab_potential_partners'), potentialPartnersCount),
      key: FeedbackTabs.POTENTIAL_PARTNERS,
      content: <PotentialPartnersContainer />,
    },
    {
      label: renderTabLabel(t('tab_partners_requests'), partnersRequestsCount),
      key: FeedbackTabs.PARTNERS_REQUESTS,
      content: <PartnersRequestsContainer />,
    },
  ];

  const limitedTabs = [
    {
      label: renderTabLabel(t('tab_users_requests'), usersRequestsCount),
      key: FeedbackTabs.USERS_REQUESTS,
      content: <UsersFeedbackContainer />,
    },
  ];

  return canManageFeedback ? tabs : limitedTabs;
};

export const getUsersRequestsTabsConfig = (t: TFunction, newCountByType: Record<FeedbackType, number>) => [
  {
    label: renderTabLabel(t('tab_contacts'), newCountByType[FeedbackType.CONTACT_PAGE] ?? 0),
    key: UsersRequestsTabs.CONTACT,
    content: <ContactSupportContainer />,
  },
  {
    label: renderTabLabel(t('tab_product_availability'), newCountByType[FeedbackType.PRODUCT_AVAILABILITY] ?? 0),
    key: UsersRequestsTabs.PRODUCT_AVAILABILITY,
    content: <ProductAvailabilityContainer />,
  },
  {
    label: renderTabLabel(t('tab_order_questions'), newCountByType[FeedbackType.ORDER_SUPPORT] ?? 0),
    key: UsersRequestsTabs.ORDER_QUESTIONS,
    content: <OrderQuestionsContainer />,
  },
];
